import React from 'react';
import { motion } from 'framer-motion';

/**
 * StrategyDiagram — case-study-specific illustrative graphic that visualises the
 * strategic direction of the engagement. Rendered as inline SVG on cream so the
 * imagery lives inside the site's editorial design language.
 *
 * Motion — a slow, staggered reveal:
 *   • Nodes fade + scale in with a per-index delay
 *   • Connecting lines / rules use a stroke-dashoffset draw
 *   • Everything begins on scroll into view (framer's `whileInView`) once
 */

const INK = '#1A1A1A';
const CREAM = '#FBFAF6';
const SUNK = '#F4F2EC';
const RULE = '#E8E4D9';
const GREEN = '#22A94F';
const GREEN_LIGHT = '#7ED4A0';
const GREEN_DEEP = '#178838';
const TERRA = '#C4633F';
const NAVY = '#0F3D22';

const EASE = [0.22, 1, 0.36, 1];

// Reusable framer variants
const nodeReveal = (i, base = 0.15) => ({
  initial: { opacity: 0, scale: 0.4 },
  whileInView: { opacity: 1, scale: 1 },
  viewport: { once: true, margin: '-60px' },
  transition: { delay: base + i * 0.09, duration: 0.7, ease: EASE },
});

const labelReveal = (i, base = 0.25) => ({
  initial: { opacity: 0, y: 6 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-60px' },
  transition: { delay: base + i * 0.09, duration: 0.7, ease: EASE },
});

const lineDraw = (i, base = 0.1) => ({
  initial: { pathLength: 0, opacity: 0 },
  whileInView: { pathLength: 1, opacity: 1 },
  viewport: { once: true, margin: '-60px' },
  transition: { delay: base + i * 0.06, duration: 0.9, ease: EASE },
});

// -----------------------------------------------------------------------------
// Sephora — card-based visualisation (no SVG illustration).
// Uses the same information architecture — Central, Global, UAE, One
// operating rhythm — but reads as an editorial card layout instead of a
// diagram. Client requested: illustration removed, cards retained.
// -----------------------------------------------------------------------------
function SephoraDiagram() {
  const cards = [
    {
      kicker: 'CENTRAL',
      title: 'The strategic ceiling',
      body: 'Brand, category framework and global commerce standards set at the centre — the baseline every market inherits.',
    },
    {
      kicker: 'GLOBAL',
      title: 'Standards, in motion',
      body: 'Global expectations flow through central and get expressed locally, on the ground — same Sephora, everywhere it lands.',
    },
    {
      kicker: 'UAE',
      title: 'One market, expressed locally',
      body: 'Search on Arabic keyboards, occasions that matter, loyalty behaviour reflecting how customers actually shop — the strategy lands the way the market reads.',
    },
    {
      kicker: 'OPERATING RHYTHM',
      title: 'Central adapts. Local flexes.',
      body: 'A working line between the two — decisions taken quickly, at the right level, without a committee. Local moves on customer moments without waiting for global sign-off.',
    },
  ];
  return (
    <div className="w-full bg-[#FBFAF6] p-6 md:p-10">
      <div className="max-w-5xl">
        <div className="serif-tight text-[32px] md:text-[44px] leading-[1] text-[#0A0A0A]">One Sephora. Expressed locally.</div>
        <div className="mt-3 mono text-[10px] md:text-[11px] tracking-[0.28em] uppercase text-[#0A0A0A]/55">CENTRAL · GLOBAL · UAE · ONE OPERATING RHYTHM</div>
      </div>
      <div className="mt-8 md:mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
        {cards.map((c, i) => (
          <motion.article
            key={c.kicker}
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.5, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
            className="relative rounded-[14px] bg-[#F4F2EC] border border-[#E8E4D9] p-5 md:p-6 hover:border-[#178838]/50 transition-colors"
            data-testid={`sephora-card-${i}`}
          >
            <div className="absolute left-5 md:left-6 top-5 md:top-6 mono text-[9px] md:text-[10px] tracking-[0.28em] text-[#178838] font-medium">
              {c.kicker}
            </div>
            <div className="mt-8 serif text-[20px] md:text-[24px] leading-[1.18] text-[#0A0A0A]">
              {c.title}
            </div>
            <div className="mt-3 text-[13.5px] md:text-[14px] leading-[1.6] text-[#0A0A0A]/75">
              {c.body}
            </div>
          </motion.article>
        ))}
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Al-Futtaim (Blue SuperApp) — hub-and-spoke.
// Larger viewBox + hub pushed down so the top spoke doesn't collide with the
// heading, and label positions are tuned for cardinal directions.
// -----------------------------------------------------------------------------
function BlueDiagram() {
  const spokes = [
    { angle: -90, label: 'Automotive',      sub: 'Toyota · Lexus · Honda' },
    { angle: -30, label: 'Retail',          sub: 'IKEA · Marks & Spencer' },
    { angle:  30, label: 'Real Estate',     sub: 'Cavendish · Festival City' },
    { angle:  90, label: 'Financial Svcs',  sub: 'Blue FAB Credit · Insurance' },
    { angle: 150, label: 'Health & Beauty', sub: 'Watsons · Faces' },
    { angle: 210, label: 'Fashion & Life',  sub: 'Zara · Trendyol · Robinsons' },
  ];
  const cx = 360;
  const cy = 300; // pushed down from 190 → 300 to give the heading breathing room
  const r = 140;
  return (
    <svg viewBox="0 0 720 560" className="w-full h-auto" role="img" aria-label="Blue SuperApp — one experience across the group">
      <rect x="0" y="0" width="720" height="560" fill={CREAM} />
      <text x="40" y="52" fill={INK} fontFamily="Fraunces, serif" fontSize="26" fontWeight="500">One customer. 200+ businesses.</text>
      <text x="40" y="76" fill={INK} opacity="0.55" fontFamily="JetBrains Mono, monospace" fontSize="10" letterSpacing="2">CROSS-BRAND LOYALTY · UNIFIED WALLET · SHARED IDENTITY</text>

      {/* Spoke connectors — animated draw */}
      {spokes.map((s, i) => {
        const rad = (s.angle * Math.PI) / 180;
        const x = cx + r * Math.cos(rad);
        const y = cy + r * Math.sin(rad);
        return (
          <motion.line
            key={`l${i}`}
            x1={cx} y1={cy} x2={x} y2={y}
            stroke={INK} opacity="0.18" strokeWidth="1"
            {...lineDraw(i, 0.15)}
          />
        );
      })}

      {/* Hub — Blue */}
      <motion.circle
        cx={cx} cy={cy} r="52" fill="#003E7E"
        {...nodeReveal(0, 0)}
      />
      <circle cx={cx} cy={cy} r="52" fill="none" stroke={GREEN_LIGHT} strokeWidth="2" strokeDasharray="4 6" opacity="0.5">
        <animateTransform attributeName="transform" type="rotate" from={`0 ${cx} ${cy}`} to={`360 ${cx} ${cy}`} dur="26s" repeatCount="indefinite" />
      </circle>
      <text x={cx} y={cy - 4} textAnchor="middle" fill={CREAM} fontFamily="Fraunces, serif" fontSize="22" fontWeight="500" style={{ pointerEvents: 'none' }}>Blue</text>
      <text x={cx} y={cy + 14} textAnchor="middle" fill={CREAM} opacity="0.7" fontFamily="JetBrains Mono, monospace" fontSize="8" letterSpacing="2" style={{ pointerEvents: 'none' }}>SUPERAPP</text>

      {/* Spoke nodes + labels */}
      {spokes.map((s, i) => {
        const rad = (s.angle * Math.PI) / 180;
        const x = cx + r * Math.cos(rad);
        const y = cy + r * Math.sin(rad);
        const labelOffset = 38;
        const labelX = cx + (r + labelOffset) * Math.cos(rad);
        const labelY = cy + (r + labelOffset) * Math.sin(rad);
        // For vertical spokes (top/bottom), always centre labels
        const isVertical = Math.abs(Math.cos(rad)) < 0.15;
        const anchor = isVertical ? 'middle' : Math.cos(rad) > 0 ? 'start' : 'end';
        // Adjust vertical text offset for top spokes so both lines sit above the node
        const isTop = s.angle === -90;
        const isBottom = s.angle === 90;
        const labelTop = isTop ? labelY - 12 : labelY;
        const subTop = isTop ? labelY + 4 : labelY + 14;
        return (
          <g key={i}>
            <motion.circle
              cx={x} cy={y} r="14"
              fill={CREAM} stroke="#003E7E" strokeWidth="1.5"
              {...nodeReveal(i + 1, 0.35)}
            />
            <motion.text x={labelX} y={labelTop} textAnchor={anchor} fill={INK} fontFamily="Inter, sans-serif" fontSize="12" fontWeight="600" {...labelReveal(i, 0.6)}>{s.label}</motion.text>
            <motion.text x={labelX} y={subTop} textAnchor={anchor} fill={INK} opacity="0.55" fontFamily="Inter, sans-serif" fontSize="9" {...labelReveal(i, 0.72)}>{s.sub}</motion.text>
            {/* Extra breathing room for bottom spoke — nothing to add, position already places label below node */}
            {isBottom && null}
          </g>
        );
      })}
    </svg>
  );
}

// -----------------------------------------------------------------------------
// MAGRABi — omnichannel eye-care: two surfaces, one customer file spine.
// -----------------------------------------------------------------------------
function MagrabiDiagram() {
  return (
    <svg viewBox="0 0 720 420" className="w-full h-auto" role="img" aria-label="A unified omnichannel eye-care experience">
      <rect x="0" y="0" width="720" height="420" fill={CREAM} />
      <text x="40" y="52" fill={INK} fontFamily="Fraunces, serif" fontSize="26" fontWeight="500">Two surfaces. One customer file.</text>
      <text x="40" y="76" fill={INK} opacity="0.55" fontFamily="JetBrains Mono, monospace" fontSize="10" letterSpacing="2">STORE · COMMERCE · SAME PRESCRIPTION EVERYWHERE</text>

      {/* Vertical spine connecting the two surfaces through the customer file */}
      <motion.line
        x1="360" y1="150" x2="360" y2="360"
        stroke={TERRA} strokeOpacity="0.4" strokeWidth="1.2" strokeDasharray="4 6"
        {...lineDraw(0, 0.15)}
      />

      {/* eCommerce vertex (top) */}
      <g transform="translate(360 155)">
        <motion.g {...nodeReveal(1, 0.4)}>
          <rect x="-72" y="-38" width="144" height="76" rx="12" fill={CREAM} stroke={TERRA} strokeWidth="1.5" />
          <rect x="-58" y="-24" width="116" height="6" rx="3" fill={INK} opacity="0.85" />
          <rect x="-58" y="-14" width="72" height="4" rx="2" fill={INK} opacity="0.4" />
          <rect x="-58" y="-6" width="34" height="34" rx="4" fill={TERRA} opacity="0.35" />
          <rect x="-20" y="-6" width="34" height="34" rx="4" fill={TERRA} opacity="0.55" />
          <rect x="18" y="-6" width="34" height="34" rx="4" fill={TERRA} opacity="0.75" />
          <text x="0" y="-52" textAnchor="middle" fill={INK} fontFamily="Fraunces, serif" fontSize="14" fontWeight="500">eCommerce</text>
          <text x="0" y="-38" textAnchor="middle" fill={INK} opacity="0.55" fontFamily="Inter, sans-serif" fontSize="10">Frames · Lenses · Care</text>
        </motion.g>
      </g>

      {/* Central node — customer file */}
      <g transform="translate(0 0)">
        <motion.g {...nodeReveal(0, 0.55)}>
          <circle cx="360" cy="255" r="46" fill={NAVY} />
          <text x="360" y="253" textAnchor="middle" fill={CREAM} fontFamily="Fraunces, serif" fontSize="15" fontWeight="500">Customer</text>
          <text x="360" y="271" textAnchor="middle" fill={CREAM} opacity="0.7" fontFamily="JetBrains Mono, monospace" fontSize="8" letterSpacing="2">FILE</text>
        </motion.g>
      </g>

      {/* In-store vertex (bottom) */}
      <g transform="translate(360 360)">
        <motion.g {...nodeReveal(2, 0.7)}>
          <rect x="-72" y="-30" width="144" height="60" rx="10" fill={CREAM} stroke={TERRA} strokeWidth="1.5" />
          <polygon points="-56,-14 -28,-24 0,-14 28,-24 56,-14 56,20 -56,20" fill={SUNK} />
          <rect x="-28" y="0" width="18" height="20" fill={TERRA} opacity="0.6" />
          <rect x="6" y="0" width="18" height="20" fill={INK} opacity="0.25" />
          <text x="0" y="44" textAnchor="middle" fill={INK} fontFamily="Fraunces, serif" fontSize="14" fontWeight="500">In-store</text>
          <text x="0" y="58" textAnchor="middle" fill={INK} opacity="0.55" fontFamily="Inter, sans-serif" fontSize="10">Fitting · Prescription · After-care</text>
        </motion.g>
      </g>
    </svg>
  );
}

// -----------------------------------------------------------------------------
// Fenix Outdoor — 10-brand constellation. viewBox enlarged, hub pushed down,
// EUROPE / USA bars removed (they overlapped bottom-node labels). Continent
// grouping is expressed as a legend chip inline with the eyebrow instead.
// -----------------------------------------------------------------------------
function FenixDiagram() {
  const brands = [
    'Fjällräven', 'Tierra', 'Hanwag', 'Primus', 'Naturkompaniet',
    'Frilufts', 'Royal Robbins', 'Brunton', 'Frilufts Ret.', 'Fenix Ret.',
  ];
  const cx = 360;
  const cy = 320; // pushed down (was 210) so top labels don't hit the heading
  const r = 150;
  return (
    <svg viewBox="0 0 720 560" className="w-full h-auto" role="img" aria-label="Ten brands. Two continents. One digital spine.">
      <rect x="0" y="0" width="720" height="560" fill={CREAM} />
      <text x="40" y="52" fill={INK} fontFamily="Fraunces, serif" fontSize="26" fontWeight="500">Ten brands. Two continents.</text>
      <text x="40" y="76" fill={INK} opacity="0.55" fontFamily="JetBrains Mono, monospace" fontSize="10" letterSpacing="2">SHARED DIGITAL SPINE · BRAND-LEVEL INDEPENDENCE</text>

      {/* Continent legend chips — inline, cannot overlap nodes below */}
      <g transform="translate(40 108)">
        <rect x="0" y="0" width="12" height="4" rx="2" fill={GREEN_DEEP} opacity="0.75" />
        <text x="20" y="6" fontFamily="JetBrains Mono, monospace" fontSize="10" fill={INK} opacity="0.7" letterSpacing="2">EUROPE · 8 BRANDS</text>
        <rect x="200" y="0" width="12" height="4" rx="2" fill={GREEN_DEEP} opacity="0.4" />
        <text x="220" y="6" fontFamily="JetBrains Mono, monospace" fontSize="10" fill={INK} opacity="0.7" letterSpacing="2">USA · 2 BRANDS</text>
      </g>

      {/* Central hub */}
      <motion.circle cx={cx} cy={cy} r="42" fill={GREEN_DEEP} {...nodeReveal(0, 0)} />
      <text x={cx} y={cy - 3} textAnchor="middle" fill={CREAM} fontFamily="Fraunces, serif" fontSize="14" fontWeight="500" style={{ pointerEvents: 'none' }}>Digital</text>
      <text x={cx} y={cy + 14} textAnchor="middle" fill={CREAM} fontFamily="Fraunces, serif" fontSize="14" fontWeight="500" style={{ pointerEvents: 'none' }}>Spine</text>

      {/* 10 brand nodes — even radial distribution */}
      {brands.map((b, i) => {
        const angle = ((i * 360) / brands.length - 90) * (Math.PI / 180);
        const x = cx + r * Math.cos(angle);
        const y = cy + r * Math.sin(angle);
        const labelR = r + 34;
        const labelX = cx + labelR * Math.cos(angle);
        const labelY = cy + labelR * Math.sin(angle) + 3;
        const isVertical = Math.abs(Math.cos(angle)) < 0.15;
        const anchor = isVertical ? 'middle' : Math.cos(angle) > 0 ? 'start' : 'end';
        return (
          <g key={i}>
            <motion.line x1={cx} y1={cy} x2={x} y2={y} stroke={INK} opacity="0.14" {...lineDraw(i, 0.15)} />
            <motion.circle cx={x} cy={y} r="10" fill={CREAM} stroke={GREEN_DEEP} strokeWidth="1.5" {...nodeReveal(i, 0.35)} />
            <motion.text x={labelX} y={labelY} textAnchor={anchor} fill={INK} fontFamily="Inter, sans-serif" fontSize="11" fontWeight="500" {...labelReveal(i, 0.6)}>{b}</motion.text>
          </g>
        );
      })}
    </svg>
  );
}

// -----------------------------------------------------------------------------
// DigitalWorks Internal — coverage → two-person review → publish
// -----------------------------------------------------------------------------
function InternalDiagram() {
  return (
    <svg viewBox="0 0 720 360" className="w-full h-auto" role="img" aria-label="Coverage → Two-Person Review → Publish">
      <rect x="0" y="0" width="720" height="360" fill={CREAM} />
      <text x="40" y="52" fill={INK} fontFamily="Fraunces, serif" fontSize="26" fontWeight="500">We ran it on ourselves first.</text>
      <text x="40" y="76" fill={INK} opacity="0.55" fontFamily="JetBrains Mono, monospace" fontSize="10" letterSpacing="2">COVERAGE · TWO-PERSON REVIEW · PUBLISHED COST</text>

      {/* Stage 01 */}
      <g transform="translate(40 130)">
        <motion.g {...nodeReveal(0, 0.1)}>
          <rect x="0" y="0" width="180" height="140" rx="14" fill={CREAM} stroke={NAVY} strokeWidth="1.5" />
          <text x="16" y="28" fill={NAVY} fontFamily="JetBrains Mono, monospace" fontSize="10" letterSpacing="2">STAGE 01</text>
          <text x="16" y="62" fill={INK} fontFamily="Fraunces, serif" fontSize="22" fontWeight="500">Coverage first</text>
          <text x="16" y="90" fill={INK} opacity="0.65" fontFamily="Inter, sans-serif" fontSize="11">End-to-end tests validated by</text>
          <text x="16" y="104" fill={INK} opacity="0.65" fontFamily="Inter, sans-serif" fontSize="11">business stakeholders.</text>
          <rect x="16" y="118" width="148" height="4" rx="2" fill={SUNK} />
          <rect x="16" y="118" width="140" height="4" rx="2" fill={NAVY} />
        </motion.g>
      </g>

      <g transform="translate(230 200)">
        <motion.g {...lineDraw(0, 0.4)}>
          <line x1="0" y1="0" x2="26" y2="0" stroke={INK} opacity="0.4" strokeWidth="1.5" />
          <polygon points="26,-4 34,0 26,4" fill={INK} opacity="0.4" />
        </motion.g>
      </g>

      {/* Stage 02 */}
      <g transform="translate(272 130)">
        <motion.g {...nodeReveal(1, 0.35)}>
          <rect x="0" y="0" width="180" height="140" rx="14" fill={CREAM} stroke={GREEN_DEEP} strokeWidth="1.5" />
          <text x="16" y="28" fill={GREEN_DEEP} fontFamily="JetBrains Mono, monospace" fontSize="10" letterSpacing="2">STAGE 02</text>
          <text x="16" y="62" fill={INK} fontFamily="Fraunces, serif" fontSize="22" fontWeight="500">Two-person integrity</text>
          <text x="16" y="90" fill={INK} opacity="0.65" fontFamily="Inter, sans-serif" fontSize="11">Every release approved by two</text>
          <text x="16" y="104" fill={INK} opacity="0.65" fontFamily="Inter, sans-serif" fontSize="11">different people.</text>
          <g transform="translate(16 116)">
            <circle cx="10" cy="10" r="9" fill={GREEN_DEEP} />
            <circle cx="28" cy="10" r="9" fill="none" stroke={GREEN_DEEP} strokeWidth="1.5" />
          </g>
        </motion.g>
      </g>

      <g transform="translate(462 200)">
        <motion.g {...lineDraw(1, 0.6)}>
          <line x1="0" y1="0" x2="26" y2="0" stroke={INK} opacity="0.4" strokeWidth="1.5" />
          <polygon points="26,-4 34,0 26,4" fill={INK} opacity="0.4" />
        </motion.g>
      </g>

      {/* Stage 03 */}
      <g transform="translate(504 130)">
        <motion.g {...nodeReveal(2, 0.6)}>
          <rect x="0" y="0" width="180" height="140" rx="14" fill={NAVY} stroke={NAVY} strokeWidth="1.5" />
          <text x="16" y="28" fill={GREEN_LIGHT} fontFamily="JetBrains Mono, monospace" fontSize="10" letterSpacing="2">STAGE 03</text>
          <text x="16" y="62" fill={CREAM} fontFamily="Fraunces, serif" fontSize="22" fontWeight="500">Published cost</text>
          <text x="16" y="90" fill={CREAM} opacity="0.75" fontFamily="Inter, sans-serif" fontSize="11">The honest number, including</text>
          <text x="16" y="104" fill={CREAM} opacity="0.75" fontFamily="Inter, sans-serif" fontSize="11">where we struggled.</text>
          <text x="16" y="126" fill={GREEN_LIGHT} fontFamily="Fraunces, serif" fontSize="14" fontWeight="500">4 months · 6 workstreams</text>
        </motion.g>
      </g>
    </svg>
  );
}

// -----------------------------------------------------------------------------
// Wiiv — one shell / one identity / one ledger / one wallet
// A layered stack visual: Client Shell → Gateway → BFF layer → Core services
// with a dedicated conversational overlay (Wiivee / Pan-App Agent).
// -----------------------------------------------------------------------------
// -----------------------------------------------------------------------------
// Wiiv — a REPRESENTATIVE member-facing platform architecture pattern.
// Deliberately generic: shows the standard layers (client, gateway,
// capability modules, core services, data platform) without exposing the
// concrete integration modes, partner tiers or service names of the actual
// programme. The one case-specific concept preserved is that a conversational
// assistant is scoped INSIDE the core, riding the same downstream APIs — a
// principle worth surfacing, without leaking the plan for it.
// -----------------------------------------------------------------------------
function WiivDiagram() {
  const NAVY_DEEP = '#0F2B54';
  const NAVY_MID  = '#284A80';
  const AGENT     = '#22A94F';
  return (
    <svg viewBox="0 0 720 500" className="w-full h-auto" role="img" aria-label="A representative member-facing platform architecture pattern">
      <rect x="0" y="0" width="720" height="500" fill={CREAM} />
      <text x="40" y="52" fill={INK} fontFamily="Fraunces, serif" fontSize="26" fontWeight="500">A representative platform pattern.</text>
      <text x="40" y="76" fill={INK} opacity="0.55" fontFamily="JetBrains Mono, monospace" fontSize="10" letterSpacing="2">CLIENT · GATEWAY · CAPABILITY MODULES · CORE SERVICES · DATA</text>

      {/* Layer 1 — Client surface */}
      <g transform="translate(40 106)">
        <motion.rect x="0" y="0" width="640" height="46" rx="10" fill={NAVY_DEEP} {...nodeReveal(0, 0.08)} />
        <text x="20" y="20" fill={CREAM} fontFamily="JetBrains Mono, monospace" fontSize="9" letterSpacing="2">CLIENT</text>
        <text x="20" y="36" fill={CREAM} fontFamily="Inter, sans-serif" fontSize="11" fontWeight="600">Mobile app · Web · one member-facing shell</text>
      </g>

      {/* Connector */}
      <motion.line x1="360" y1="152" x2="360" y2="168" stroke={NAVY_DEEP} strokeOpacity="0.5" strokeWidth="1.2" initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: 0.14 }} />

      {/* Layer 2 — API Gateway */}
      <g transform="translate(40 168)">
        <motion.rect x="0" y="0" width="640" height="38" rx="8" fill={NAVY_MID} {...nodeReveal(1, 0.18)} />
        <text x="20" y="16" fill={CREAM} fontFamily="JetBrains Mono, monospace" fontSize="9" letterSpacing="2">API GATEWAY</text>
        <text x="20" y="30" fill={CREAM} fontFamily="Inter, sans-serif" fontSize="10">Auth · rate limits · routing</text>
      </g>

      {/* Connector */}
      <motion.line x1="360" y1="206" x2="360" y2="222" stroke={NAVY_DEEP} strokeOpacity="0.4" strokeWidth="1.2" initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: 0.24 }} />

      {/* Layer 3 — Capability modules row (four generic capability groups) */}
      <g transform="translate(40 222)">
        <text fill={INK} opacity="0.65" fontFamily="JetBrains Mono, monospace" fontSize="9" letterSpacing="2">CAPABILITY MODULES · MULTIPLE CAPABILITIES · ONE SURFACE</text>
        {[
          { t: 'Commerce', l: 'Catalogue & basket' },
          { t: 'Payments', l: 'Wallet & rails' },
          { t: 'Rewards',  l: 'Earn & redeem' },
          { t: 'Concierge', l: 'Assisted flows' },
        ].map((tile, i) => (
          <g key={tile.t} transform={`translate(${i * 162} 12)`}>
            <motion.rect x="0" y="0" width="152" height="58" rx="8" fill={CREAM} stroke={NAVY_MID} strokeWidth="1.4" {...nodeReveal(i + 2, 0.28)} />
            <text x="12" y="22" fill={NAVY_MID} fontFamily="JetBrains Mono, monospace" fontSize="9" letterSpacing="2">{tile.t.toUpperCase()}</text>
            <text x="12" y="42" fill={INK} opacity="0.75" fontFamily="Inter, sans-serif" fontSize="10">{tile.l}</text>
          </g>
        ))}
      </g>

      {/* Connector */}
      <motion.line x1="360" y1="298" x2="360" y2="316" stroke={NAVY_DEEP} strokeOpacity="0.4" strokeWidth="1.2" initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: 0.38 }} />

      {/* Layer 4 — Core services (shared substrate) */}
      <g transform="translate(40 316)">
        <text fill={INK} opacity="0.65" fontFamily="JetBrains Mono, monospace" fontSize="9" letterSpacing="2">CORE SERVICES · SHARED BY EVERY MODULE</text>
        {[
          { t: 'Identity',       l: 'One profile per member' },
          { t: 'Wallet',         l: 'Balances & entitlements' },
          { t: 'Notifications',  l: 'Delivery across channels' },
          { t: 'Compliance',     l: 'Audit trail & policy' },
        ].map((tile, i) => (
          <g key={tile.t} transform={`translate(${i * 162} 12)`}>
            <motion.rect x="0" y="0" width="152" height="58" rx="8" fill={NAVY_DEEP} opacity="0.92" {...nodeReveal(i + 6, 0.44)} />
            <text x="12" y="22" fill={CREAM} fontFamily="JetBrains Mono, monospace" fontSize="9" letterSpacing="2">{tile.t.toUpperCase()}</text>
            <text x="12" y="42" fill={CREAM} opacity="0.85" fontFamily="Inter, sans-serif" fontSize="10">{tile.l}</text>
          </g>
        ))}
      </g>

      {/* Layer 5 — Data platform */}
      <g transform="translate(40 396)">
        <motion.rect x="0" y="0" width="640" height="34" rx="8" fill={CREAM} stroke={NAVY_DEEP} strokeWidth="1.5" strokeDasharray="4 4" {...nodeReveal(10, 0.6)} />
        <text x="20" y="14" fill={NAVY_DEEP} fontFamily="JetBrains Mono, monospace" fontSize="9" letterSpacing="2">DATA PLATFORM</text>
        <text x="20" y="28" fill={INK} opacity="0.75" fontFamily="Inter, sans-serif" fontSize="10">Event stream · analytics · behavioural spine</text>
      </g>

      {/* Overlay — conversational assistant (the one case-specific idea kept:
          the assistant is scoped INSIDE the platform, not bolted on later). */}
      <g transform="translate(40 448)">
        <motion.rect x="0" y="0" width="640" height="34" rx="12" fill={AGENT} {...nodeReveal(11, 0.7)} />
        <circle cx="24" cy="17" r="6" fill={CREAM} opacity="0.28" />
        <circle cx="24" cy="17" r="3" fill={CREAM} />
        <text x="42" y="14" fill={CREAM} fontFamily="JetBrains Mono, monospace" fontSize="9" letterSpacing="2">CONVERSATIONAL ASSISTANT</text>
        <text x="42" y="28" fill={CREAM} fontFamily="Inter, sans-serif" fontSize="10">Scoped inside the platform — reuses the same downstream APIs</text>
      </g>
    </svg>
  );
}

// -----------------------------------------------------------------------------
// NorthLadder — anonymised B2B marketplace pattern
// Higher-level: buyer paths → shared engine → supplier signals + reverse loop.
// -----------------------------------------------------------------------------
function NorthLadderDiagram() {
  const AMBER = '#B6844B';
  const NLNAVY = '#0D2438';
  return (
    <svg viewBox="0 0 720 460" className="w-full h-auto" role="img" aria-label="NorthLadder — anonymised marketplace engine">
      <rect x="0" y="0" width="720" height="460" fill={CREAM} />
      <text x="40" y="52" fill={INK} fontFamily="Fraunces, serif" fontSize="26" fontWeight="500">One engine. Two anonymous sides.</text>
      <text x="40" y="76" fill={INK} opacity="0.55" fontFamily="JetBrains Mono, monospace" fontSize="10" letterSpacing="2">BUYER · ENGINE · SUPPLIER · DEMAND-INTELLIGENCE LOOP</text>

      {/* Buyer side (left) */}
      <g transform="translate(40 130)">
        <motion.rect x="0" y="0" width="180" height="220" rx="12" fill={CREAM} stroke={NLNAVY} strokeWidth="1.5" {...nodeReveal(0, 0.1)} />
        <text x="16" y="28" fill={NLNAVY} fontFamily="JetBrains Mono, monospace" fontSize="10" letterSpacing="2">BUYER PLATFORM</text>
        <text x="16" y="58" fill={INK} fontFamily="Fraunces, serif" fontSize="16" fontWeight="500">Two paths in</text>

        <g transform="translate(16 76)">
          <rect x="0" y="0" width="148" height="46" rx="6" fill="#F4F2EC" />
          <text x="10" y="18" fill={NLNAVY} fontFamily="JetBrains Mono, monospace" fontSize="8" letterSpacing="2">PATH A</text>
          <text x="10" y="34" fill={INK} fontFamily="Inter, sans-serif" fontSize="11">Browse pooled inventory</text>
        </g>
        <g transform="translate(16 132)">
          <rect x="0" y="0" width="148" height="46" rx="6" fill="#F4F2EC" />
          <text x="10" y="18" fill={NLNAVY} fontFamily="JetBrains Mono, monospace" fontSize="8" letterSpacing="2">PATH B</text>
          <text x="10" y="34" fill={INK} fontFamily="Inter, sans-serif" fontSize="11">Post a requirement</text>
        </g>
        <text x="16" y="200" fill={INK} opacity="0.5" fontFamily="Inter, sans-serif" fontSize="10">Buyer never sees supplier</text>
      </g>

      {/* Engine (centre) */}
      <g transform="translate(260 130)">
        <motion.rect x="0" y="0" width="200" height="220" rx="12" fill={NLNAVY} {...nodeReveal(1, 0.28)} />
        <text x="16" y="28" fill={AMBER} fontFamily="JetBrains Mono, monospace" fontSize="10" letterSpacing="2">ANONYMISED ENGINE</text>
        <text x="16" y="60" fill={CREAM} fontFamily="Fraunces, serif" fontSize="18" fontWeight="500">Same engine.</text>
        <text x="16" y="82" fill={CREAM} fontFamily="Fraunces, serif" fontSize="18" fontWeight="500">Both paths.</text>

        {[
          'Anonymised pricing model',
          'Fractional supplier allocation',
          'Strict anonymity enforced',
          'Unified order ledger',
        ].map((l, i) => (
          <g key={l} transform={`translate(16 ${112 + i * 24})`}>
            <circle cx="4" cy="8" r="2.5" fill={AMBER} />
            <text x="14" y="12" fill={CREAM} opacity="0.9" fontFamily="Inter, sans-serif" fontSize="10">{l}</text>
          </g>
        ))}
      </g>

      {/* Supplier side (right) */}
      <g transform="translate(500 130)">
        <motion.rect x="0" y="0" width="180" height="220" rx="12" fill={CREAM} stroke={NLNAVY} strokeWidth="1.5" {...nodeReveal(2, 0.46)} />
        <text x="16" y="28" fill={NLNAVY} fontFamily="JetBrains Mono, monospace" fontSize="10" letterSpacing="2">SUPPLIER PLATFORM</text>
        <text x="16" y="58" fill={INK} fontFamily="Fraunces, serif" fontSize="16" fontWeight="500">Bid Now + Signals</text>

        <g transform="translate(16 76)">
          <rect x="0" y="0" width="148" height="46" rx="6" fill="#F4F2EC" />
          <text x="10" y="18" fill={NLNAVY} fontFamily="JetBrains Mono, monospace" fontSize="8" letterSpacing="2">MATCHED</text>
          <text x="10" y="34" fill={INK} fontFamily="Inter, sans-serif" fontSize="11">Bid pool · asc by price</text>
        </g>
        <g transform="translate(16 132)">
          <rect x="0" y="0" width="148" height="46" rx="6" fill="#F4F2EC" />
          <text x="10" y="18" fill={NLNAVY} fontFamily="JetBrains Mono, monospace" fontSize="8" letterSpacing="2">DEMAND</text>
          <text x="10" y="34" fill={INK} fontFamily="Inter, sans-serif" fontSize="11">Unmet buyer signal</text>
        </g>
        <text x="16" y="200" fill={INK} opacity="0.5" fontFamily="Inter, sans-serif" fontSize="10">Supplier never sees buyer</text>
      </g>

      {/* Flow arrows */}
      <motion.g {...lineDraw(0, 0.6)}>
        <line x1="220" y1="240" x2="260" y2="240" stroke={NLNAVY} strokeWidth="1.5" />
        <polygon points="260,236 268,240 260,244" fill={NLNAVY} />
      </motion.g>
      <motion.g {...lineDraw(1, 0.7)}>
        <line x1="460" y1="240" x2="500" y2="240" stroke={NLNAVY} strokeWidth="1.5" />
        <polygon points="500,236 508,240 500,244" fill={NLNAVY} />
      </motion.g>
      {/* Reverse demand-intelligence loop */}
      <motion.path
        d="M 500 300 Q 380 380 260 300"
        stroke={AMBER} strokeWidth="1.5" strokeDasharray="4 4" fill="none"
        {...lineDraw(2, 0.85)}
      />
      <text x="360" y="370" textAnchor="middle" fill={AMBER} fontFamily="JetBrains Mono, monospace" fontSize="9" letterSpacing="1.5">DEMAND-INTELLIGENCE LOOP</text>

      {/* Metrics footer */}
      <g transform="translate(40 400)">
        <line x1="0" y1="0" x2="640" y2="0" stroke={RULE} />
        <g transform="translate(0 24)">
          <text fontFamily="Fraunces, serif" fontSize="22" fill={GREEN_DEEP}>€2M+</text>
          <text y="20" fontFamily="Inter, sans-serif" fontSize="10" fill={INK} opacity="0.6">Monthly GMV positioning</text>
        </g>
        <g transform="translate(180 24)">
          <text fontFamily="Fraunces, serif" fontSize="22" fill={GREEN_DEEP}>500+</text>
          <text y="20" fontFamily="Inter, sans-serif" fontSize="10" fill={INK} opacity="0.6">Active SKUs modelled</text>
        </g>
        <g transform="translate(360 24)">
          <text fontFamily="Fraunces, serif" fontSize="22" fill={GREEN_DEEP}>&lt;5s</text>
          <text y="20" fontFamily="Inter, sans-serif" fontSize="10" fill={INK} opacity="0.6">Fractional allocation SLA</text>
        </g>
      </g>
    </svg>
  );
}

const map = {
  wiiv: WiivDiagram,
  northladder: NorthLadderDiagram,
  sephora: SephoraDiagram,
  'al-futtaim': BlueDiagram,
  magrabi: MagrabiDiagram,
  fenix: FenixDiagram,
  'digitalworks-internal': InternalDiagram,
};

export default function StrategyDiagram({ slug, className = '' }) {
  const Comp = map[slug];
  if (!Comp) return null;
  return (
    <div className={`w-full ${className}`} data-testid={`strategy-diagram-${slug}`}>
      <Comp />
    </div>
  );
}
